import qs from 'qs';

import type {
  CharterPricingInventoryApiRow,
  CharterPricingInventoryRow,
  ListCharterPricingInventoryParams,
  ListCharterPricingInventoryResponse,
  UpsertCharterPricingInventoryBody,
  UpsertCharterPricingInventoryResponse,
} from '~/types/charterPricingInventory';

import { json_headers, request } from './common';

const BASE_PATH = `${window.configs.API_HOST}/api/flights-charter/v1/fare-families/fnf`;

type RequestOptions = {
  signal?: AbortSignal;
};

const contractToUiCabinMap = {
  ECONOMY: 'economy',
  PREMIUM_ECONOMY: 'premium_economy',
  LUX_ROW: 'lux_row',
  LUX_ROW_PAX_ONE: 'lux_row_pax_one',
  BUSINESS: 'business',
} as const;

const uiToContractCabinMap = {
  economy: 'ECONOMY',
  premium_economy: 'PREMIUM_ECONOMY',
  lux_row: 'LUX_ROW',
  lux_row_pax_one: 'LUX_ROW_PAX_ONE',
  business: 'BUSINESS',
} as const;

function toUiCabinClass(cabinClass: string): CharterPricingInventoryRow['cabin'] {
  return contractToUiCabinMap[cabinClass as keyof typeof contractToUiCabinMap] ?? 'economy';
}

export function toContractCabinClass(
  cabinClass: CharterPricingInventoryRow['cabin'],
): UpsertCharterPricingInventoryBody['cabinClass'] {
  return uiToContractCabinMap[cabinClass];
}

function getPricingPrice(
  pricing: Array<CharterPricingInventoryApiRow['pricing'][number]>,
  paxType: 'ADT' | 'CHD' | 'INF',
): number {
  return pricing.find((pricingRow) => pricingRow.paxType === paxType)?.price ?? 0;
}

function toCharterPricingInventoryRow(pricingInventory: CharterPricingInventoryApiRow): CharterPricingInventoryRow {
  const depAirport = pricingInventory.flightInstance?.depAirport ?? '';
  const arrAirport = pricingInventory.flightInstance?.arrAirport ?? '';

  return {
    id: pricingInventory.fnfFareId,
    flightInstanceId: pricingInventory.flightInstanceId,
    depAirport,
    arrAirport,
    route: depAirport && arrAirport ? `${depAirport} -> ${arrAirport}` : '',
    date: pricingInventory.flightInstance?.depDate ?? '',
    cabin: toUiCabinClass(pricingInventory.cabinClass),
    availableDaysBefore: pricingInventory.availableDaysBefore,
    waitlistSeat: pricingInventory.allocation,
    totalBooked: pricingInventory.booked,
    adultPrice: getPricingPrice(pricingInventory.pricing, 'ADT'),
    childPrice: getPricingPrice(pricingInventory.pricing, 'CHD'),
    infantPrice: getPricingPrice(pricingInventory.pricing, 'INF'),
    employee: pricingInventory.bookedEmployee,
    friendAndFamily: pricingInventory.bookedReferral,
    openForSale: pricingInventory.allocation - pricingInventory.booked,
  };
}

export async function listCharterPricingInventory(
  params: ListCharterPricingInventoryParams = {},
  options: RequestOptions = {},
): Promise<ListCharterPricingInventoryResponse> {
  const query = qs.stringify(params, { skipNulls: true });
  const url = query ? `${BASE_PATH}?${query}` : BASE_PATH;

  const response: { status: number; message: string; result: Array<CharterPricingInventoryApiRow> } = await request(
    url,
    {
      ...options,
      method: 'GET',
    },
  );

  return {
    status: response.status,
    message: response.message,
    result: response.result.map((pricingInventory) => toCharterPricingInventoryRow(pricingInventory)),
  };
}

export async function upsertCharterPricingInventory(
  body: UpsertCharterPricingInventoryBody,
): Promise<UpsertCharterPricingInventoryResponse> {
  const response: { status: number; message: string; result: CharterPricingInventoryApiRow } = await request(
    BASE_PATH,
    {
      method: 'PUT',
      headers: json_headers,
      body: JSON.stringify(body),
    },
  );

  return {
    status: response.status,
    message: response.message,
    result: toCharterPricingInventoryRow(response.result),
  };
}
